import React, { useState, useEffect } from 'react';
import { stationConnectionAPI, routePathAPI } from '../services/api';
import { StationConnection, StationConnectionDetail, RoutePathResponse } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent
} from '@mui/material';
import { Add, Delete, Search, FilterList, Clear, Route } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const StationConnections: React.FC = () => {
  const [connections, setConnections] = useState<StationConnectionDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    fromStationId: '',
    toStationId: '',
    lineId: '',
    distance: '',
    duration: '',
    isInterchange: false,
    interchangeLineId: '',
    interchangeTime: '',
    isActive: true
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLine, setSelectedLine] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  
  // Route Path Popup State
  const [showRoutePathModal, setShowRoutePathModal] = useState(false);
  const [routePathData, setRoutePathData] = useState<RoutePathResponse | null>(null);
  const [routePathLoading, setRoutePathLoading] = useState(false);
  const [routePathError, setRoutePathError] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<StationConnectionDetail | null>(null);
  
  // Route Path Actions State
  const [regeneratingRoutePath, setRegeneratingRoutePath] = useState(false);
  const [deletingRoutePath, setDeletingRoutePath] = useState(false);
  
  // Bulk Generation State
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkGenerationResult, setBulkGenerationResult] = useState<{
    generated: number;
    total: number;
    errors: string[];
    message: string;
  } | null>(null);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await stationConnectionAPI.getAll();
      setConnections(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  // Filter connections based on search and filters
  const filteredConnections = connections.filter(conn => {
    const matchesSearch = searchTerm === '' || 
      conn.fromStation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conn.toStation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conn.fromStation.stationCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conn.toStation.stationCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLine = selectedLine === '' || conn.line.id === selectedLine;
    const matchesCity = selectedCity === '' || 
      conn.fromStation.cityName === selectedCity || 
      conn.toStation.cityName === selectedCity;
    
    return matchesSearch && matchesLine && matchesCity;
  });

  // Get unique lines and cities for filters
  const uniqueLines = Array.from(new Set(connections.map(conn => conn.line.id)));
  const uniqueCities = Array.from(new Set([
    ...connections.map(conn => conn.fromStation.cityName),
    ...connections.map(conn => conn.toStation.cityName)
  ]));

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedLine('');
    setSelectedCity('');
  };

  const handleTextFieldInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError('');
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const handleAddConnection = async () => {
    if (!form.fromStationId.trim() || !form.toStationId.trim() || !form.lineId.trim() || !form.distance.trim() || !form.duration.trim()) {
      setFormError('From Station, To Station, Line, Distance, and Duration are required.');
      return;
    }
    setSaving(true);
    try {
      await stationConnectionAPI.create({
        fromStationId: form.fromStationId.trim(),
        toStationId: form.toStationId.trim(),
        lineId: form.lineId.trim(),
        distance: parseInt(form.distance, 10),
        duration: parseInt(form.duration, 10),
        isInterchange: form.isInterchange,
        interchangeLineId: form.interchangeLineId.trim() || undefined,
        interchangeTime: form.interchangeTime ? parseInt(form.interchangeTime, 10) : undefined,
        isActive: form.isActive
      });
      setShowModal(false);
      setForm({ fromStationId: '', toStationId: '', lineId: '', distance: '', duration: '', isInterchange: false, interchangeLineId: '', interchangeTime: '', isActive: true });
      setSuccessMsg('Station connection created successfully!');
      fetchConnections();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to create station connection');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) return;
    setDeletingId(id);
    try {
      await stationConnectionAPI.delete(id);
      setSuccessMsg('Station connection deleted successfully!');
      fetchConnections();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete station connection');
    } finally {
      setDeletingId(null);
    }
  };

  const handleShowRoutePath = async (connection: StationConnectionDetail) => {
    setSelectedConnection(connection);
    setShowRoutePathModal(true);
    setRoutePathLoading(true);
    setRoutePathError(null);
    setRoutePathData(null);
    
    try {
      const response = await routePathAPI.findRoutePath({
        sourceStationId: connection.fromStation.id,
        destinationStationId: connection.toStation.id
      });
      setRoutePathData(response.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setRoutePathError(error.response?.data?.message || error.message || 'Failed to load route path');
    } finally {
      setRoutePathLoading(false);
    }
  };

  const handleBulkGenerateRoutePaths = async () => {
    if (!window.confirm('This will generate route paths for all station connections that don\'t have route paths. This may take several minutes. Continue?')) {
      return;
    }
    
    setBulkGenerating(true);
    setBulkGenerationResult(null);
    setError(null);
    
    try {
      const response = await routePathAPI.generateAllRoutePaths();
      setBulkGenerationResult(response.data);
      setSuccessMsg(response.data.message);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to generate route paths');
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleRegenerateRoutePath = async () => {
    if (!selectedConnection || !routePathData) return;
    
    if (!window.confirm('This will fetch fresh route data from Google Maps API. Continue?')) {
      return;
    }
    
    setRegeneratingRoutePath(true);
    setRoutePathError(null);
    
    try {
      const response = await routePathAPI.regenerateRoutePathByStations({
        sourceStationId: selectedConnection.fromStation.id,
        destinationStationId: selectedConnection.toStation.id
      });
      setRoutePathData(response.data);
      setSuccessMsg('Route path regenerated successfully!');
    } catch (e: unknown) {
      const error = e as ApiError;
      setRoutePathError(error.response?.data?.message || error.message || 'Failed to regenerate route path');
    } finally {
      setRegeneratingRoutePath(false);
    }
  };

  const handleDeleteRoutePath = async () => {
    if (!routePathData) return;
    
    if (!window.confirm('This will delete the route path. Continue?')) {
      return;
    }
    
    setDeletingRoutePath(true);
    setRoutePathError(null);
    
    try {
      await routePathAPI.deleteRoutePath(routePathData.id);
      setRoutePathData(null);
      setSuccessMsg('Route path deleted successfully!');
    } catch (e: unknown) {
      const error = e as ApiError;
      setRoutePathError(error.response?.data?.message || error.message || 'Failed to delete route path');
    } finally {
      setDeletingRoutePath(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Station Connections Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button 
            variant="outlined" 
            onClick={fetchConnections}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleBulkGenerateRoutePaths}
            disabled={bulkGenerating || loading}
            startIcon={bulkGenerating ? <CircularProgress size={16} /> : <Route />}
          >
            {bulkGenerating ? 'Generating...' : 'Generate All'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setShowModal(true)}
            sx={{ borderRadius: 2 }}
          >
            Add Connection
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {bulkGenerationResult && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setBulkGenerationResult(null)}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            {bulkGenerationResult.message}
          </Typography>
          {bulkGenerationResult.errors.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                Errors: {bulkGenerationResult.errors.length}
              </Typography>
              <Box mt={0.5} maxHeight="100px" overflow="auto">
                {bulkGenerationResult.errors.map((error, index) => (
                  <Typography key={index} variant="caption" display="block" color="error.main">
                    • {error}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Alert>
      )}

      {/* Statistics Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {connections.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Connections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {connections.filter(conn => conn.isActive).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Connections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {connections.filter(conn => conn.isInterchange).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Interchange Connections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {uniqueLines.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Metro Lines
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FilterList color="action" />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search stations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Line</InputLabel>
                <Select
                  value={selectedLine}
                  onChange={(e) => setSelectedLine(e.target.value)}
                  label="Line"
                >
                  <MenuItem value="">All Lines</MenuItem>
                  {uniqueLines.map(lineId => {
                    const line = connections.find(conn => conn.line.id === lineId)?.line;
                    return (
                      <MenuItem key={lineId} value={lineId}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            width={16}
                            height={16}
                            borderRadius="50%"
                            sx={{ backgroundColor: line?.color }}
                          />
                          {line?.name} ({line?.code})
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>City</InputLabel>
                <Select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  label="City"
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {uniqueCities.map(city => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={clearFilters}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box p={2} borderBottom={1} borderColor="divider">
            <Typography variant="body2" color="text.secondary">
              Showing {filteredConnections.length} of {connections.length} connections
            </Typography>
          </Box>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>From Station</TableCell>
                  <TableCell>To Station</TableCell>
                  <TableCell>Line</TableCell>
                  <TableCell>Distance (m)</TableCell>
                  <TableCell>Duration (min)</TableCell>
                  <TableCell>Interchange</TableCell>
                  <TableCell>Interchange Line</TableCell>
                  <TableCell>Interchange Time</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConnections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {connections.length === 0 ? 'No station connections found.' : 'No connections match the current filters.'}
                    </TableCell>
                  </TableRow>
                ) : filteredConnections.map(conn => (
                  <TableRow key={conn.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {conn.fromStation.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conn.fromStation.stationCode} • {conn.fromStation.cityName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {conn.toStation.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conn.toStation.stationCode} • {conn.toStation.cityName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={conn.line.name} 
                        size="small" 
                        sx={{ 
                          backgroundColor: conn.line.color, 
                          color: 'white',
                          fontWeight: 'medium'
                        }} 
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        {conn.line.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{conn.distance}m</TableCell>
                    <TableCell>{conn.duration}min</TableCell>
                    <TableCell>
                      <Chip 
                        label={conn.isInterchange ? 'Yes' : 'No'} 
                        color={conn.isInterchange ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {conn.interchangeLineId ? (
                        <Chip 
                          label="Interchange" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {conn.interchangeTime ? `${conn.interchangeTime}min` : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={conn.isActive ? 'Yes' : 'No'} 
                        color={conn.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Route Path">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleShowRoutePath(conn)}
                          >
                            <Route />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(conn.id!)}
                            disabled={deletingId === conn.id}
                          >
                            {deletingId === conn.id ? <CircularProgress size={16} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Station Connection</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="From Station ID" name="fromStationId" value={form.fromStationId} onChange={handleTextFieldInput} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="To Station ID" name="toStationId" value={form.toStationId} onChange={handleTextFieldInput} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Line ID" name="lineId" value={form.lineId} onChange={handleTextFieldInput} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Distance (meters)" name="distance" value={form.distance} onChange={handleTextFieldInput} fullWidth required type="number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Duration (minutes)" name="duration" value={form.duration} onChange={handleTextFieldInput} fullWidth required type="number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="isInterchange" checked={form.isInterchange} onChange={handleCheckbox} />}
                label="Interchange"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Interchange Line ID" name="interchangeLineId" value={form.interchangeLineId} onChange={handleTextFieldInput} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Interchange Time (minutes)" name="interchangeTime" value={form.interchangeTime} onChange={handleTextFieldInput} fullWidth type="number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="isActive" checked={form.isActive} onChange={handleCheckbox} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleAddConnection} variant="contained" color="primary" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Route Path Details Modal */}
      <Dialog 
        open={showRoutePathModal} 
        onClose={() => setShowRoutePathModal(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Route color="primary" />
            Route Path Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {routePathError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRoutePathError(null)}>
              {routePathError}
            </Alert>
          )}
          
          {selectedConnection && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Connection Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    From Station
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedConnection.fromStation.name} ({selectedConnection.fromStation.stationCode})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    To Station
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedConnection.toStation.name} ({selectedConnection.toStation.stationCode})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Line
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedConnection.line.name} ({selectedConnection.line.code})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Distance & Duration
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedConnection.distance}m • {selectedConnection.duration}min
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {routePathLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : routePathData ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Route Coordinates ({routePathData.routeCoordinates.length} points)
              </Typography>
              
              <Box 
                sx={{ 
                  maxHeight: '400px', 
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2
                }}
              >
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Sequence</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Latitude</TableCell>
                        <TableCell>Longitude</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {routePathData.routeCoordinates.map((coord, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{coord.sequence}</TableCell>
                          <TableCell>
                            <Chip 
                              label={coord.description} 
                              size="small" 
                              color={coord.description === 'source_station' || coord.description === 'destination_station' ? 'primary' : 'default'}
                              variant={coord.description === 'source_station' || coord.description === 'destination_station' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>{coord.latitude.toFixed(6)}</TableCell>
                          <TableCell>{coord.longitude.toFixed(6)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Route ID:</strong> {routePathData.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> {routePathData.isActive ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
            </Box>
          ) : !routePathError && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="body1" color="text.secondary">
                No route path data available
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {routePathData && (
            <>
              <Button 
                onClick={handleRegenerateRoutePath}
                disabled={regeneratingRoutePath}
                color="primary"
                variant="outlined"
                startIcon={regeneratingRoutePath ? <CircularProgress size={16} /> : <Route />}
              >
                Regenerate
              </Button>
              <Button 
                onClick={handleDeleteRoutePath}
                disabled={deletingRoutePath}
                color="error"
                variant="outlined"
                startIcon={deletingRoutePath ? <CircularProgress size={16} /> : <Delete />}
              >
                Delete
              </Button>
            </>
          )}
          <Button onClick={() => setShowRoutePathModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StationConnections; 